import { useState, useCallback, useEffect } from "react";

/**
 * Hook to get the current section of a document based on the current section in the viewport.
 * @param sections - Array of section elements
 * @param options - IntersectionObserver options
 * @returns Index of the currently intersecting section
 */
const useIntersectingSections = (
  sections: Element[],
  options?: IntersectionObserverInit
) => {
  const [isAtTop, setIsAtTop] = useState(true);
  const [intersections, setIntersections] = useState<number[]>([]);

  // Current section index is the section furthest down the page of the currently intersecting sections
  const intersectingSectionIndex = isAtTop ? 0 : Math.max(0, ...intersections);

  const handleScroll = () => {
    setIsAtTop(window.scrollY === 0);
  };

  useEffect(() => {
    setIsAtTop(window.scrollY === 0);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        const entryIndex = sections.indexOf(entry.target);

        // Add to the list of intersecting sections
        if (entry.isIntersecting) {
          setIntersections((previous) => {
            return [
              ...previous.filter((index) => {
                return index !== entryIndex;
              }),
              entryIndex,
            ];
          });
        }

        // Remove from the list of intersecting sections
        if (!entry.isIntersecting) {
          setIntersections((previous) => {
            return previous.filter((index) => {
              return index !== entryIndex;
            });
          });
        }
      }
    },
    [sections]
  );

  // Create intersection observers for each section
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, options);
    for (const section of sections) {
      observer.observe(section);
    }

    return () => {
      for (const section of sections) {
        observer.unobserve(section);
      }
    };
  }, [sections, options, handleIntersection]);

  return intersectingSectionIndex;
};

export default useIntersectingSections;
